import {
  NAVBAR_GUTTER_PX,
  NAVBAR_HEIGHT_PX,
} from "@/constants/nav-bar-constant";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
// import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
// import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
// import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import DuoIcon from "@mui/icons-material/Duo";
// import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
// import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
// import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
// import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
// import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import csvColor from "/public/csv-color.svg";
import csvDisable from "/public/csv-disable.svg";
import patientColor from "/public/patient-color.svg";
import patientDisable from "/public/patient-detail-disable.svg";
import type { Privilege } from "@/context/constant";
import { PRIMITIVE, px } from "@/tokens/design-tokens";
import {
  ECARE_CHART_COLORS,
  ECARE_COLOR,
  ECARE_LETTER_SPACING,
  ECARE_RADIUS,
  ECARE_SHADOW,
} from "@/tokens/ecare-tokens";
import type {
  AddPatientOption,
  CreateAction,
  EcareApp,
  EcareAppGroup,
  NavBarIcon,
} from "./nav-bar-types";

const t = PRIMITIVE.typography;

/**
 * The bar is a fixed brand-blue surface in both themes, so what sits on it is
 * tinted white rather than pulled from the theme's text tokens (§13).
 */
const ON_BAR_ALPHA = {
  MUTED: "rgba(255, 255, 255, 0.82)",
  HOVER: "rgba(255, 255, 255, 0.14)",
  RULE: "rgba(255, 255, 255, 0.22)",
} as const;

export const NAV_BAR_HEIGHT_PX = NAVBAR_HEIGHT_PX;

export const NAV_BAR_GUTTER_PX = NAVBAR_GUTTER_PX;

export const NAV_BAR_LAYOUT = {
  BAR_Z_INDEX: 90,
  BAR_PADDING_X: "20px",
  BRAND_GAP: "2px",
  BRAND_TO_TABS_GAP: "28px",
  LOGO_WIDTH: 116,
  LOGO_HEIGHT: 24,

  TAB_STRIP_GAP: "2px",
  TAB_HEIGHT: "40px",
  TAB_PADDING_X: "12px",
  TAB_GAP: "6px",
  // Active tab is a card flush with the page, so only the top corners round.
  TAB_RADIUS: `${px(ECARE_RADIUS.md)} ${px(ECARE_RADIUS.md)} 0 0`,
  TAB_ICON_SIZE: "18px",

  MENU_OFFSET_Y: "6px",
  MENU_MIN_WIDTH: "196px",

  ACTIONS_GAP: "14px",
  ACTION_ICON_SIZE: "20px",
  ACTION_BUTTON_SIZE: "32px",
  SEARCH_ICON_SIZE: 18,

  CREATE_HEIGHT: "30px",
  CREATE_PADDING_X: "10px",
  CREATE_RADIUS: px(ECARE_RADIUS.xs),
  CREATE_GAP: "4px",
  CREATE_ICON_SIZE: "18px",

  AVATAR_SIZE: "30px",
  AVATAR_FONT_SIZE: "12px",
  AVATAR_LARGE_SIZE: "70px",
  AVATAR_LARGE_FONT_SIZE: "24px",

  PROFILE_MENU_TOP_GAP: 1.2,
  PROFILE_MENU_WIDTH: "300px",
  PROFILE_MENU_HEIGHT: "400px",
  PROFILE_MENU_HEIGHT_COMPACT: "334px",

  // App switcher popover follows `.appswitch-pop` (styleguide §13).
  APP_SWITCHER_WIDTH: "384px",
  APP_SWITCHER_PADDING: "16px",
  APP_SWITCHER_RADIUS: px(ECARE_RADIUS.lg),
  APP_SWITCHER_SHADOW: ECARE_SHADOW.xl,
  APP_SECTION_GAP: "20px",
  APP_SECTION_TITLE_GAP: "12px",
  APP_GRID_COLUMNS: 3,
  APP_GRID_GAP: "8px",
  APP_TILE_PADDING: "10px 6px",
  APP_TILE_GAP: "8px",
  APP_TILE_RADIUS: px(ECARE_RADIUS.md),
  APP_TILE_ICON_BOX: "52px",
  APP_TILE_ICON_SIZE: "24px",
  APP_WAITLIST_PADDING: "1px 8px",
  APP_WAITLIST_RADIUS: px(ECARE_RADIUS.xs),

  ADD_PATIENT_BODY_WIDTH: "26.75rem",
  ADD_PATIENT_BODY_HEIGHT: "240.5px",
  ADD_PATIENT_BODY_TOP_GAP: "5rem",
  ADD_PATIENT_NEXT_WIDTH: "6.25rem",
} as const;

export const NAV_BAR_COLORS = {
  BAR_BG: ECARE_COLOR.accent.primary,
  ON_BAR: ECARE_COLOR.text.onAccent,
  ON_BAR_MUTED: ON_BAR_ALPHA.MUTED,
  TAB_HOVER_BG: ON_BAR_ALPHA.HOVER,
  TAB_ACTIVE_BG: ECARE_COLOR.bg.surface,
  TAB_ACTIVE_TEXT: ECARE_COLOR.accent.primary,
  DIVIDER: ON_BAR_ALPHA.RULE,
  AVATAR_BG: ECARE_COLOR.bg.selected,
  AVATAR_TEXT: ECARE_COLOR.text.primary,
  SURFACE: ECARE_COLOR.bg.surface,
  MENU_ITEM_TEXT: ECARE_COLOR.text.secondary,
  APP_SWITCHER_BG: ECARE_COLOR.bg.subtle,
  APP_SECTION_TITLE: ECARE_COLOR.text.secondary,
  APP_TILE_NAME: ECARE_COLOR.text.primary,
  APP_TILE_HOVER_BG: ECARE_COLOR.bg.selected,
  APP_TILE_ICON_COLOR: ECARE_COLOR.text.onAccent,
  APP_WAITLIST_BG: ECARE_COLOR.bg.selected,
  APP_WAITLIST_TEXT: ECARE_COLOR.text.secondary,
} as const;

/** Soft-accent pill (§9 `.pill-accent`) — shared by the app and role chips. */
export const NAV_BAR_ACCENT_PILL = {
  backgroundColor: ECARE_COLOR.accent.soft,
  color: ECARE_COLOR.accent.softText,
  border: `1px solid ${ECARE_COLOR.accent.softBorder}`,
} as const;

export const NAV_BAR_TYPOGRAPHY = {
  /** Tabs, menu items, action labels, user name, app tiles. */
  LABEL_FONT_SIZE: px(t.fontSize.body.b5),
  LABEL_FONT_WEIGHT: t.fontWeight.medium,
  /** Waitlist pill on an app tile. */
  CAPTION_FONT_SIZE: px(t.fontSize.body.b6),
  /** App tile names and switcher section titles — 12px/600 (§13). */
  APP_NAME_FONT_WEIGHT: t.fontWeight.semiBold,
  /** Provider-group line under the brand — `.overline-sm` (§2.2). */
  OVERLINE_FONT_SIZE: "11px",
  OVERLINE_FONT_WEIGHT: t.fontWeight.semiBold,
  OVERLINE_LETTER_SPACING: ECARE_LETTER_SPACING.overline,
} as const;

/** Shared popover surface for every menu the bar opens. */
export const NAV_BAR_MENU_PAPER_SX = {
  mt: NAV_BAR_LAYOUT.MENU_OFFSET_Y,
  minWidth: NAV_BAR_LAYOUT.MENU_MIN_WIDTH,
} as const;

export const NAV_BAR_LABELS = {
  LOGO_ALT: "eCarehealth",
  SEARCH_TOOLTIP: "Search Patient by First Name, Last Name",
  CREATE: "Create",
  APP_SWITCHER_TOOLTIP: "eCare applications",
  APP_WAITLIST: "Waitlist",
  PROVIDER_GROUP_TOOLTIP: "Provider group",
  PROVIDER_NAME_PREFIX: "Dr.",
  EHR_INVITE_SUCCESS:
    "The invitation link has been sent successfully to your email.",
  IMPORT_PATIENTS_TITLE: "Import Patients",
  ADD_PATIENT_TITLE: "Add Patient",
  ADD_PATIENT_NEXT: "Next",
  PROVIDER_GROUP_FIELD: "PROVIDER GROUP",
  LOGGED_LOCATION_FIELD: "LOGGED LOCATION",
} as const;

export const NAV_BAR_POPUP_IDS = {
  PROVIDER_GROUP: "nav-bar-provider-group",
  CREATE_MENU: "nav-bar-create-menu",
  PROFILE_MENU: "nav-bar-profile-menu",
  SUB_MENU: "nav-bar-sub-menu",
} as const;

export const NAV_BAR_ASSETS = {
  SEARCH_ICON: "/search.svg",
} as const;

export const PROVIDER_GROUP_STORAGE_KEY = "pgName";

export const PROFILE_LOG_OUT_VALUE = "log_out";
export const PROFILE_HELP_VALUE = "help";

export const CREATE_ACTIONS = {
  PATIENT: "patient",
  APPOINTMENT: "appointment",
  INSTANT_APPOINTMENT: "instant-appointment",
} as const;

export const CREATE_ACTION_PERMISSIONS: Record<CreateAction, Privilege> = {
  [CREATE_ACTIONS.PATIENT]: "Patient Registration",
  [CREATE_ACTIONS.APPOINTMENT]: "Appointment Scheduling",
  [CREATE_ACTIONS.INSTANT_APPOINTMENT]: "Instant Appointment",
};

export const CREATE_ACTION_LABELS: Record<CreateAction, string> = {
  [CREATE_ACTIONS.PATIENT]: "New Patient",
  [CREATE_ACTIONS.APPOINTMENT]: "New Appointment",
  [CREATE_ACTIONS.INSTANT_APPOINTMENT]: "Instant Appointment",
};

export const CREATE_ACTION_ICONS: Record<CreateAction, NavBarIcon> = {
  [CREATE_ACTIONS.PATIENT]: PersonAddOutlinedIcon,
  [CREATE_ACTIONS.APPOINTMENT]: TodayOutlinedIcon,
  [CREATE_ACTIONS.INSTANT_APPOINTMENT]: DuoIcon,
};

export const ADD_PATIENT_SOURCES = {
  CSV: "csv",
  EHR: "ehr",
  PATIENT: "patient",
} as const;

/** Import-from-EHR is withheld until the integration ships. */
export const ADD_PATIENT_OPTIONS: readonly AddPatientOption[] = [
  {
    source: ADD_PATIENT_SOURCES.CSV,
    label: "Upload CSV File",
    activeIcon: csvColor,
    inactiveIcon: csvDisable,
  },
  {
    source: ADD_PATIENT_SOURCES.PATIENT,
    label: "Enter Patient Details",
    activeIcon: patientColor,
    inactiveIcon: patientDisable,
  },
];

export const ADD_PATIENT_TILE_COLUMNS = 6;

export const ECARE_APP_GROUPS = {
  APPLICATIONS: "applications",
  AI_AGENTS: "ai-agents",
} as const;

/** Section order in the switcher follows this map's key order. */
export const ECARE_APP_GROUP_LABELS: Record<EcareAppGroup, string> = {
  [ECARE_APP_GROUPS.APPLICATIONS]: "Applications",
  [ECARE_APP_GROUPS.AI_AGENTS]: "AI Agents",
};

/** Tile tints stay inside the chart palette — no new hues (§18). */
const APP_TINT = {
  BLUE: ECARE_CHART_COLORS[0],
  TERRACOTTA: ECARE_CHART_COLORS[1],
  GREEN: ECARE_CHART_COLORS[2],
  PURPLE: ECARE_CHART_COLORS[4],
  AMBER: ECARE_CHART_COLORS[5],
  TEAL: ECARE_CHART_COLORS[6],
  ROSE: ECARE_CHART_COLORS[7],
} as const;

/**
 * Apps offered by the 9-dot switcher. Tiles without a `url` render as static
 * until product supplies the destination.
 */
export const ECARE_APPS: readonly EcareApp[] = [
  {
    id: "ecare-health",
    name: "eCareHealth",
    group: ECARE_APP_GROUPS.APPLICATIONS,
    icon: AddOutlinedIcon,
    color: APP_TINT.ROSE,
    isCurrent: true,
  },
  // {
  //   id: "ecare-md",
  //   name: "eCareMD",
  //   group: ECARE_APP_GROUPS.APPLICATIONS,
  //   icon: MedicalServicesOutlinedIcon,
  //   color: APP_TINT.TEAL,
  // },
  // {
  //   id: "ecare-ltc",
  //   name: "eCareLTC",
  //   group: ECARE_APP_GROUPS.APPLICATIONS,
  //   icon: HomeOutlinedIcon,
  //   color: APP_TINT.GREEN,
  // },
  // {
  //   id: "ecare-rcm",
  //   name: "eCareRCM",
  //   group: ECARE_APP_GROUPS.APPLICATIONS,
  //   icon: PaidOutlinedIcon,
  //   color: APP_TINT.AMBER,
  //   isWaitlisted: true,
  // },
  // {
  //   id: "ecare-crm",
  //   name: "eCareCRM",
  //   group: ECARE_APP_GROUPS.APPLICATIONS,
  //   icon: GroupOutlinedIcon,
  //   color: APP_TINT.PURPLE,
  // },
  // {
  //   id: "ecare-connect",
  //   name: "eCareConnect",
  //   group: ECARE_APP_GROUPS.APPLICATIONS,
  //   icon: ChatBubbleOutlineOutlinedIcon,
  //   color: APP_TINT.BLUE,
  //   isWaitlisted: true,
  // },
  // {
  //   id: "ecare-hrms",
  //   name: "eCareHRMS",
  //   group: ECARE_APP_GROUPS.APPLICATIONS,
  //   icon: BadgeOutlinedIcon,
  //   color: APP_TINT.PURPLE,
  // },
  // {
  //   id: "ecare-vitalsq",
  //   name: "eCareVitalsQ",
  //   group: ECARE_APP_GROUPS.APPLICATIONS,
  //   icon: MonitorHeartOutlinedIcon,
  //   color: APP_TINT.GREEN,
  // },
  // {
  //   id: "ecare-assist",
  //   name: "eCareAssist",
  //   group: ECARE_APP_GROUPS.AI_AGENTS,
  //   icon: AutoAwesomeOutlinedIcon,
  //   color: APP_TINT.ROSE,
  // },
  // {
  //   id: "ecare-scribe",
  //   name: "eCareScribe",
  //   group: ECARE_APP_GROUPS.AI_AGENTS,
  //   icon: AutoAwesomeOutlinedIcon,
  //   color: APP_TINT.BLUE,
  // },
  // {
  //   id: "ecare-medcoder",
  //   name: "eCareMedCoder",
  //   group: ECARE_APP_GROUPS.AI_AGENTS,
  //   icon: AutoAwesomeOutlinedIcon,
  //   color: APP_TINT.PURPLE,
  // },
  // {
  //   id: "ecare-compliance",
  //   name: "eCareCompliance",
  //   group: ECARE_APP_GROUPS.AI_AGENTS,
  //   icon: AutoAwesomeOutlinedIcon,
  //   color: APP_TINT.GREEN,
  // },
  // {
  //   id: "ecare-revenue",
  //   name: "eCareRevenue",
  //   group: ECARE_APP_GROUPS.AI_AGENTS,
  //   icon: AutoAwesomeOutlinedIcon,
  //   color: APP_TINT.TERRACOTTA,
  // },
];

export const AppSwitcherIcon = AppsOutlinedIcon;
